import { createContext, useContext, useEffect, useState } from "react";

const LangContext = createContext({ lang: "en", setLang: () => {} });

export const META = {
  en: {
    title: "CRESSCO DAYA UTAMA | Indonesian Commodity Export & Trading Company",
    desc: "CRESSCO DAYA UTAMA is an Indonesian commodity trading and export company — coconut charcoal briquettes, coffee, tuna, red dragon fruit, ginger and cloves, sourced through trusted Indonesian supply partners with flexible trade terms.",
  },
  id: {
    title: "CRESSCO DAYA UTAMA | Perusahaan Ekspor & Trading Komoditas Indonesia",
    desc: "CRESSCO DAYA UTAMA adalah perusahaan trading dan ekspor komoditas Indonesia — briket arang tempurung kelapa, kopi, tuna, buah naga merah, jahe, dan cengkeh melalui mitra pasokan terpercaya dengan syarat perdagangan fleksibel.",
  },
};

export const LangProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem("cressco-lang") || "en";
    } catch {
      return "en";
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("cressco-lang", lang);
    } catch {}
    document.documentElement.lang = lang;
  }, [lang]);
  return <LangContext.Provider value={{ lang, setLang }}>{children}</LangContext.Provider>;
};

export const useLang = () => useContext(LangContext);
export const useT = () => T[useContext(LangContext).lang];

export const T = {
  en: {
    nav: { home: "Home", about: "About Us", products: "Products", partners: "Our Partners", quality: "Quality", markets: "Markets", contact: "Contact", quote: "Request a Quote" },
    hero: {
      overline: "CRESSCO DAYA UTAMA — Indonesian Commodity Export & Trading",
      line1: "Connecting Indonesia's Finest Products",
      line2: "to Global Markets",
      sub: "Indonesian Commodity Export & Trading",
      body: "We source and export high-quality Indonesian commodities through reliable supply partners, flexible trade terms, and buyer-specific requirements.",
      cta1: "Explore Our Products",
      cta2: "Request a Quote",
      trust: ["Quality Products", "Trusted Supply Partners", "Global Markets", "Flexible Trade Terms"],
      flow: "Indonesia → Supply → Global Markets",
    },
    marquee: ["COCONUT CHARCOAL BRIQUETTES", "INDONESIAN COFFEE", "TUNA", "RED DRAGON FRUIT", "GINGER", "CLOVES", "FLEXIBLE TRADE TERMS", "FOB · CIF · CFR", "L/C · T/T", "INDONESIAN ORIGIN"],
    productsSection: {
      label: "Our Products",
      title: "Premium Indonesian Commodities",
      sub: "Natural Resources. Global Opportunities.",
      view: "View Product",
      quote: "Request Quote",
    },
    terms: {
      label: "Trade Terms",
      title: "Flexible Trade Terms",
      sub: "We adapt to your requirements.",
      items: [
        { t: "Payment Terms", v: "L/C | T/T | As Agreed" },
        { t: "Shipping Terms", v: "FOB | CIF | CFR | As Agreed" },
        { t: "Product Specification", v: "According to Buyer Requirement" },
        { t: "Packaging", v: "According to Buyer Specification" },
      ],
      note: "We work closely with buyers to align product specifications, packaging, payment methods and shipping arrangements according to transaction requirements.",
    },
    about: {
      label: "About CRESSCO",
      title: "Your Trusted Partner in Indonesian Commodities",
      p1: "CRESSCO DAYA UTAMA is an Indonesian trading and export company connecting quality local commodities with buyers in international and domestic markets.",
      p2: "We work with trusted supply partners across Indonesia to provide reliable products, flexible sourcing solutions, and professional trade coordination.",
      p3: "Our approach is simple: understand the buyer's requirements, identify the appropriate supply, coordinate quality and documentation, and support the transaction from sourcing through delivery.",
      cta: "Learn More About Us",
    },
    partners: {
      label: "Our Supply Network",
      title: "From Indonesian Sources to Global Buyers",
      sub: "Products are sourced through selected Indonesian partners — farms, fisheries, processors and manufacturers.",
      steps: ["Farmers / Producers", "Supply Partners", "Quality Control", "CRESSCO DAYA UTAMA", "Export / Logistics", "Global Buyers"],
    },
    quality: {
      label: "Quality",
      title: "Quality & Reliable Supply",
      sub: "Quality requirements vary by market and buyer. CRESSCO coordinates with supply partners to meet agreed product specifications and documentation requirements.",
      cards: [
        { t: "Product Quality", x: "Specifications agreed with the buyer before order confirmation." },
        { t: "Supplier Verification", x: "Products sourced through selected, trusted Indonesian supply partners." },
        { t: "Buyer Specification", x: "Grade, size, packaging and labeling follow buyer requirements." },
        { t: "Quality Inspection", x: "Pre-shipment inspection can be arranged according to transaction requirements." },
        { t: "Documentation", x: "COA and export documents prepared per product and destination." },
        { t: "Traceability", x: "Supply origin can be traced back to the partner level." },
      ],
      note: "Certificates, COA and supporting documents: available upon request.",
    },
    markets: {
      label: "Markets",
      title: "From Indonesia to the World",
      sub: "Connecting Indonesian commodities with international and domestic buyers.",
      regions: ["Asia", "Middle East", "Europe", "Americas", "Domestic Indonesia"],
      disclaimer: "Markets we serve or target based on buyer demand.",
      origin: "INDONESIA",
      routes: ["MIDDLE EAST", "EUROPE", "AMERICAS"],
    },
    rfq: {
      label: "Request a Quote",
      title: "Request a Quote",
      sub: "Tell us what you need — our team responds with specifications and a quotation.",
      f: { name: "Name *", company: "Company Name", email: "Email", whatsapp: "WhatsApp", country: "Country", product: "Product", quantity: "Quantity", spec: "Specification / Grade", port: "Destination Port", shipping: "Shipping Terms", payment: "Payment Terms", packaging: "Packaging Requirements", message: "Message" },
      ph: { name: "Your full name", company: "Company name", email: "name@company.com", whatsapp: "+1 ...", country: "e.g. Saudi Arabia", quantity: "e.g. 1 × 40ft FCL", spec: "e.g. grade, size, moisture", port: "e.g. Jeddah Islamic Port", packaging: "e.g. 10 kg cartons, private label", message: "Tell us about your requirements…" },
      shippingOptions: ["FOB", "CIF", "CFR", "Other / Discuss"],
      paymentOptions: ["L/C", "T/T", "Other / Discuss"],
      otherProduct: "Other / Multiple products",
      submit: "Send Inquiry",
      note: "YOUR INQUIRY OPENS DIRECTLY IN WHATSAPP — NO DATA IS STORED.",
      toast: "Opening WhatsApp with your inquiry…",
      msgHeader: "Hello CRESSCO team, I would like to request a quote.",
      msgLabels: { name: "Name", company: "Company", email: "Email", whatsapp: "WhatsApp", country: "Country", product: "Product", quantity: "Quantity", spec: "Specification", port: "Destination Port", shipping: "Shipping", payment: "Payment", packaging: "Packaging" },
    },
    contact: {
      label: "Contact",
      title: "Let's Work Together",
      copy: "Looking for reliable Indonesian products? Tell us what you need and our team will help coordinate the appropriate supply solution.",
      cta: "Contact CRESSCO",
      wa: "Chat on WhatsApp",
      emailLabel: "Email",
      waLabel: "WhatsApp",
      webLabel: "Website",
    },
    detail: {
      overview: "Product Overview",
      variants: "Available Variants",
      specs: "Product Specification",
      origin: "Origin",
      quality: "Quality",
      packaging: "Packaging",
      moq: "MOQ",
      capacity: "Supply Capacity",
      payment: "Payment Terms",
      shipping: "Shipping Terms",
      documents: "Documents / Certifications",
      quote: "Request a Quote",
      whatsapp: "WhatsApp Us",
      back: "All Products",
      onRequest: "Available upon request",
      buyerSpec: "According to buyer specification",
      paymentTerms: "L/C | T/T | As Agreed",
      shippingTerms: "FOB | CIF | CFR | As Agreed",
      viewCoa: "View COA Document",
      note: "Product availability is subject to buyer requirements and supply confirmation.",
      originValue: "Indonesia",
    },
    footer: {
      tagline: "Indonesian commodity trading and export company — connecting reliable Indonesian supply partners with international and domestic buyers.",
      navTitle: "Navigation",
      productsTitle: "Products",
      contactTitle: "Contact",
      rights: "© 2026 CRESSCO DAYA UTAMA. All Rights Reserved.",
      privacy: "Privacy Policy",
      terms: "Terms of Service",
      motto: "Trading for a Better Tomorrow.",
    },
    wa: {
      generic: "Hello CRESSCO team, I would like to inquire about Indonesian commodities.",
      product: (name) => `Hello CRESSCO team, I would like to request a quote for ${name}.`,
    },
  },
  id: {
    nav: { home: "Beranda", about: "Tentang Kami", products: "Produk", partners: "Mitra Kami", quality: "Kualitas", markets: "Pasar", contact: "Kontak", quote: "Minta Penawaran" },
    hero: {
      overline: "CRESSCO DAYA UTAMA — Ekspor & Trading Komoditas Indonesia",
      line1: "Menghubungkan Produk Terbaik Indonesia",
      line2: "ke Pasar Global",
      sub: "Ekspor & Trading Komoditas Indonesia",
      body: "Kami memasok dan mengekspor komoditas Indonesia berkualitas tinggi melalui mitra pasokan terpercaya, syarat perdagangan fleksibel, dan spesifikasi sesuai kebutuhan buyer.",
      cta1: "Lihat Produk Kami",
      cta2: "Minta Penawaran",
      trust: ["Produk Berkualitas", "Mitra Pasokan Terpercaya", "Pasar Global", "Syarat Perdagangan Fleksibel"],
      flow: "Indonesia → Pasokan → Pasar Global",
    },
    marquee: ["BRIKET ARANG TEMPURUNG KELAPA", "KOPI INDONESIA", "TUNA", "BUAH NAGA MERAH", "JAHE", "CENGKEH", "SYARAT PERDAGANGAN FLEKSIBEL", "FOB · CIF · CFR", "L/C · T/T", "ASLI INDONESIA"],
    productsSection: {
      label: "Produk Kami",
      title: "Komoditas Premium Indonesia",
      sub: "Sumber Daya Alam. Peluang Global.",
      view: "Lihat Produk",
      quote: "Minta Penawaran",
    },
    terms: {
      label: "Syarat Perdagangan",
      title: "Syarat Perdagangan Fleksibel",
      sub: "Kami menyesuaikan dengan kebutuhan Anda.",
      items: [
        { t: "Syarat Pembayaran", v: "L/C | T/T | Sesuai Kesepakatan" },
        { t: "Syarat Pengiriman", v: "FOB | CIF | CFR | Sesuai Kesepakatan" },
        { t: "Spesifikasi Produk", v: "Sesuai Kebutuhan Buyer" },
        { t: "Kemasan", v: "Sesuai Spesifikasi Buyer" },
      ],
      note: "Kami bekerja sama dengan buyer untuk menyelaraskan spesifikasi produk, kemasan, metode pembayaran, dan pengaturan pengiriman sesuai kebutuhan transaksi.",
    },
    about: {
      label: "Tentang CRESSCO",
      title: "Mitra Terpercaya Anda untuk Komoditas Indonesia",
      p1: "CRESSCO DAYA UTAMA adalah perusahaan trading dan ekspor Indonesia yang menghubungkan komoditas lokal berkualitas dengan buyer di pasar internasional dan domestik.",
      p2: "Kami bekerja dengan mitra pasokan terpercaya di seluruh Indonesia untuk menyediakan produk yang andal, solusi sourcing yang fleksibel, dan koordinasi perdagangan yang profesional.",
      p3: "Pendekatan kami sederhana: memahami kebutuhan buyer, mengidentifikasi pasokan yang tepat, mengoordinasikan kualitas dan dokumentasi, serta mendukung transaksi dari sourcing hingga pengiriman.",
      cta: "Pelajari Lebih Lanjut",
    },
    partners: {
      label: "Jaringan Pasokan Kami",
      title: "Dari Sumber Indonesia ke Buyer Global",
      sub: "Produk diperoleh melalui mitra Indonesia terpilih — petani, perikanan, pemroses, dan produsen.",
      steps: ["Petani / Produsen", "Mitra Pasokan", "Kontrol Kualitas", "CRESSCO DAYA UTAMA", "Ekspor / Logistik", "Buyer Global"],
    },
    quality: {
      label: "Kualitas",
      title: "Kualitas & Pasokan Terpercaya",
      sub: "Kebutuhan kualitas bervariasi menurut pasar dan buyer. CRESSCO berkoordinasi dengan mitra pasokan untuk memenuhi spesifikasi produk dan dokumentasi yang disepakati.",
      cards: [
        { t: "Kualitas Produk", x: "Spesifikasi disepakati dengan buyer sebelum konfirmasi order." },
        { t: "Verifikasi Supplier", x: "Produk diperoleh melalui mitra pasokan Indonesia yang terpilih dan terpercaya." },
        { t: "Spesifikasi Buyer", x: "Grade, ukuran, kemasan, dan label mengikuti kebutuhan buyer." },
        { t: "Inspeksi Kualitas", x: "Inspeksi pra-pengiriman dapat diatur sesuai kebutuhan transaksi." },
        { t: "Dokumentasi", x: "COA dan dokumen ekspor disiapkan per produk dan tujuan." },
        { t: "Ketertelusuran", x: "Asal pasokan dapat dilacak hingga level mitra." },
      ],
      note: "Sertifikat, COA, dan dokumen pendukung: tersedia sesuai permintaan.",
    },
    markets: {
      label: "Pasar",
      title: "Dari Indonesia ke Dunia",
      sub: "Menghubungkan komoditas Indonesia dengan buyer internasional dan domestik.",
      regions: ["Asia", "Timur Tengah", "Eropa", "Amerika", "Domestik Indonesia"],
      disclaimer: "Pasar yang kami layani atau bidik berdasarkan permintaan buyer.",
      origin: "INDONESIA",
      routes: ["TIMUR TENGAH", "EROPA", "AMERIKA"],
    },
    rfq: {
      label: "Minta Penawaran",
      title: "Minta Penawaran",
      sub: "Beritahu kami kebutuhan Anda — tim kami merespons dengan spesifikasi dan penawaran.",
      f: { name: "Nama *", company: "Nama Perusahaan", email: "Email", whatsapp: "WhatsApp", country: "Negara", product: "Produk", quantity: "Jumlah", spec: "Spesifikasi / Grade", port: "Pelabuhan Tujuan", shipping: "Syarat Pengiriman", payment: "Syarat Pembayaran", packaging: "Kebutuhan Kemasan", message: "Pesan" },
      ph: { name: "Nama lengkap Anda", company: "Nama perusahaan", email: "nama@perusahaan.com", whatsapp: "+62 ...", country: "mis. Arab Saudi", quantity: "mis. 1 × 40ft FCL", spec: "mis. grade, ukuran, kadar air", port: "mis. Pelabuhan Jeddah", packaging: "mis. karton 10 kg, private label", message: "Ceritakan kebutuhan Anda…" },
      shippingOptions: ["FOB", "CIF", "CFR", "Lainnya / Diskusikan"],
      paymentOptions: ["L/C", "T/T", "Lainnya / Diskusikan"],
      otherProduct: "Lainnya / Beberapa produk",
      submit: "Kirim Inquiry",
      note: "INQUIRY ANDA TERBUKA LANGSUNG DI WHATSAPP — TIDAK ADA DATA YANG DISIMPAN.",
      toast: "Membuka WhatsApp dengan inquiry Anda…",
      msgHeader: "Halo tim CRESSCO, saya ingin meminta penawaran.",
      msgLabels: { name: "Nama", company: "Perusahaan", email: "Email", whatsapp: "WhatsApp", country: "Negara", product: "Produk", quantity: "Jumlah", spec: "Spesifikasi", port: "Pelabuhan Tujuan", shipping: "Pengiriman", payment: "Pembayaran", packaging: "Kemasan" },
    },
    contact: {
      label: "Kontak",
      title: "Mari Bekerja Sama",
      copy: "Mencari produk Indonesia yang terpercaya? Beritahu kami kebutuhan Anda dan tim kami akan membantu mengoordinasikan solusi pasokan yang tepat.",
      cta: "Hubungi CRESSCO",
      wa: "Chat via WhatsApp",
      emailLabel: "Email",
      waLabel: "WhatsApp",
      webLabel: "Website",
    },
    detail: {
      overview: "Gambaran Produk",
      variants: "Varian Tersedia",
      specs: "Spesifikasi Produk",
      origin: "Asal",
      quality: "Kualitas",
      packaging: "Kemasan",
      moq: "MOQ",
      capacity: "Kapasitas Pasokan",
      payment: "Syarat Pembayaran",
      shipping: "Syarat Pengiriman",
      documents: "Dokumen / Sertifikasi",
      quote: "Minta Penawaran",
      whatsapp: "WhatsApp Kami",
      back: "Semua Produk",
      onRequest: "Tersedia sesuai permintaan",
      buyerSpec: "Sesuai spesifikasi buyer",
      paymentTerms: "L/C | T/T | Sesuai Kesepakatan",
      shippingTerms: "FOB | CIF | CFR | Sesuai Kesepakatan",
      viewCoa: "Lihat Dokumen COA",
      note: "Ketersediaan produk tergantung kebutuhan buyer dan konfirmasi pasokan.",
      originValue: "Indonesia",
    },
    footer: {
      tagline: "Perusahaan trading dan ekspor komoditas Indonesia — menghubungkan mitra pasokan Indonesia terpercaya dengan buyer internasional dan domestik.",
      navTitle: "Navigasi",
      productsTitle: "Produk",
      contactTitle: "Kontak",
      rights: "© 2026 CRESSCO DAYA UTAMA. Seluruh Hak Cipta Dilindungi.",
      privacy: "Kebijakan Privasi",
      terms: "Syarat Layanan",
      motto: "Trading for a Better Tomorrow.",
    },
    wa: {
      generic: "Halo tim CRESSCO, saya ingin menanyakan komoditas Indonesia.",
      product: (name) => `Halo tim CRESSCO, saya ingin meminta penawaran untuk ${name}.`,
    },
  },
};
